/**
 * Soft-safe Move 3: Position step panel.
 */
import {
  t,
} from '../../i18n/strings'
import {
  formatSeatCode,
  formatSeatFullAccess,
  getPositions,
  positionHint,
  seatOrderLine,
} from '../../lib/poker'
import {
  getState,
  isHeroSeatPicked,
  pickHeroSeat,
} from '../../state'
import {
  el,
} from '../dom'
import {
  renderTableViz,
} from '../tableViz'
import {
  explainOnly,
  playInfoFromHand,
  shouldShowSeatAbbrevHint,
} from './helpers'


export function renderPosition(): HTMLElement {
  const ui = t()
  const { hand } = getState()
  const positions = getPositions(hand.playerCount)
  // Softwise M10c: no seat is pre-chosen for the user. Until a tap, all
  // seats carry equal weight (the stored default is never shown as picked).
  const pickedSeat = isHeroSeatPicked() ? hand.heroPosition : ''
  const hint = pickedSeat ? positionHint(pickedSeat, hand.playerCount) : ''
  const showAbbrevHint = shouldShowSeatAbbrevHint()

  const table = renderTableViz({
    playerCount: hand.playerCount,
    heroPosition: pickedSeat,
    mode: 'heroPick',
    onSelectSeat: (pos) => {
      pickHeroSeat(pos)
    },
    playInfo: playInfoFromHand(hand),
    // Softwise M6: seat pick ≠ stack/blinds edit (info stays read-only)
  })

  const chipGrid = el(
    'div',
    {
      className: 'positionGrid',
      attrs: { role: 'group', 'aria-label': ui.positionTitle },
    },
    positions.map((pos) => {
      const selected = pickedSeat === pos
      const fullAccess = formatSeatFullAccess(pos, hand.playerCount)
      const code = formatSeatCode(pos, hand.playerCount)
      return el(
        'button',
        {
          className: [
            'positionBtn',
            selected ? 'positionBtnActive' : '',
          ]
            .filter(Boolean)
            .join(' '),
          attrs: {
            type: 'button',
            title: fullAccess,
            'aria-label': fullAccess,
            'aria-pressed': selected ? 'true' : 'false',
          },
          on: {
            click: () => {
              pickHeroSeat(pos)
            },
          },
        },
        [
          el('span', { className: 'positionBtnCode', text: code }),
          el('span', {
            className: 'positionBtnHint',
            text: positionHint(pos, hand.playerCount) || pos,
          }),
        ],
      )
    }),
  )

  // List seats — collapsed <details>, default closed (do not hide completely).
  const fallback = el('details', { className: 'tableVizFallbackDetails' }, [
    el('summary', { text: ui.tableVizListFallback }),
    el('div', { className: 'tableVizFallbackBody' }, [chipGrid]),
  ])

  return el('section', { className: 'panel' }, [
    el('h2', { className: 'panelTitle', attrs: { tabindex: -1 }, text: ui.seatQuestion }),
    explainOnly(
      el('p', {
        className: 'panelHint',
        text: ui.positionHint(hand.playerCount, hint),
      }),
    ),
    showAbbrevHint
      ? explainOnly(
          el('p', {
            className: 'muted seatAbbrevHint',
            text: ui.seatAbbrevHint,
            attrs: { role: 'status' },
          }),
        )
      : null,
    el('p', {
      className: 'seatOrderHint',
      text: seatOrderLine(hand.playerCount),
    }),
    table,
    el('p', {
      className: 'muted tableVizTapHint',
      text: ui.tableVizTapHint,
    }),
    fallback,
    // Softwise M10c: no 「다음」 hint — tapping a seat advances immediately.
  ])
}
